import { useHasAccess } from "./useHasAccess";
import { queries } from "../../shared/queries";
import { useQuery, useZero } from "@rocicorp/zero/react";
import { useState } from "react";
import { sharedMutators } from "../../shared/mutators";

export type MoveRequest = {
  workflowId: string;
  targetSpaceId: string;
  targetSpaceName: string;
};

export function useMoveWorkflow(): {
  requestMove: (args: MoveRequest) => void;
  pending: MoveRequest | null;
  canMove: boolean;
  isLive: boolean;
  ready: boolean;
  confirmMove: () => void;
  cancelMove: () => void;
} {
  const z = useZero();
  const [pending, setPending] = useState<MoveRequest | null>(null);

  const [workflow, workflowDetails] = useQuery(
    pending
      ? queries.workflowMainRoutes({ workflowId: pending.workflowId })
      : undefined
  );
  const canWriteSource = useHasAccess("space:write", workflow?.spaceId);
  const canWriteTarget = useHasAccess("space:write", pending?.targetSpaceId);
  const canPushLive = useHasAccess("space:push_live", workflow?.spaceId);
  const isLive = !!workflow?.branches;
  const canMove =
    !!workflow && canWriteSource && canWriteTarget && (!isLive || canPushLive);

  const requestMove = (args: MoveRequest) => {
    setPending(args);
  };

  const confirmMove = () => {
    if (!pending || !canMove) return;
    z.mutate(
      sharedMutators.moveWorkflow({
        workflowId: pending.workflowId,
        spaceId: pending.targetSpaceId,
      })
    );
    setPending(null);
  };

  const cancelMove = () => {
    setPending(null);
  };

  return {
    requestMove,
    pending,
    canMove,
    isLive,
    ready: workflowDetails.type === "complete",
    confirmMove,
    cancelMove,
  };
}
